import { useCallback, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import { getConfig } from "../lib/runtimeConfig";

/**
 * AUTH_MODE=gateway: Explorer no longer talks to Auth0 at all. Identity comes from the shared
 * session cookie apps/auth issues (see its README) -- this component's only job is asking
 * apps/auth's GET /session/introspect whether that cookie is currently valid, and translating
 * the answer into the same shape Auth0AuthBridge provides, so every existing consumer
 * (AppContent, SecureRoute, Login, Logout, ...) needs no changes at all.
 *
 * `credentials: "include"` is required on every fetch here: the SPA's origin
 * (explorer.<deployment>.organizer.ninja) and the auth app's origin
 * (auth.<deployment>.organizer.ninja) are different origins even though they share a cookie
 * domain (see apps/auth's SESSION_COOKIE_DOMAIN) -- without it the browser never sends the
 * cookie cross-origin at all.
 */
export function GatewayAuthBridge({ children }) {
  const authAppUrl = (getConfig("AUTH_APP_URL") || "").replace(/\/+$/, "");

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(undefined);
  const [expiresAt, setExpiresAt] = useState(undefined);

  const refreshSession = useCallback(async () => {
    try {
      const response = await fetch(`${authAppUrl}/session/introspect`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUser({ sub: data.sub, email: data.email });
        setExpiresAt(data.exp ? data.exp * 1000 : undefined);
        setIsAuthenticated(true);
        setError(undefined);
      } else {
        // 401 is the ordinary "not logged in" state, not an error -- matching how Auth0's own
        // `error` field is reserved for actual auth failures, not "no session yet".
        setUser(null);
        setExpiresAt(undefined);
        setIsAuthenticated(false);
      }
    } catch (e) {
      // A network failure reaching the auth app IS an error worth surfacing -- Login.js's
      // authError branch exists for exactly this.
      setUser(null);
      setExpiresAt(undefined);
      setIsAuthenticated(false);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [authAppUrl]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const loginWithRedirect = useCallback(
    async (options = {}) => {
      const returnPath = options?.appState?.returnTo || "/";
      const target = new URL(returnPath, window.location.origin).toString();
      window.location.assign(
        `${authAppUrl}/login?redirect_uri=${encodeURIComponent(target)}`
      );
    },
    [authAppUrl]
  );

  const logout = useCallback(
    async (options = {}) => {
      const returnTo = options?.logoutParams?.returnTo || window.location.origin;
      window.location.assign(
        `${authAppUrl}/logout?redirect_uri=${encodeURIComponent(returnTo)}`
      );
    },
    [authAppUrl]
  );

  // No bearer token exists under gateway mode -- identity travels via the session cookie,
  // which the browser attaches automatically. axios.js's getApiAccessToken() already treats a
  // falsy return here as "no Authorization header", which is exactly what every gateway-mode
  // request wants (see lib/axios.js's withCredentials handling for the cookie side of this).
  const getAccessTokenSilently = useCallback(async () => "", []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        error,
        expiresAt,
        logout,
        loginWithRedirect,
        getAccessTokenSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
